import { IFormField } from "@/api/businessLinesApi";
import { v4 as uuidv4 } from "uuid";
import { FiPlus } from "react-icons/fi";
import { useEffect, useRef } from "react";

export default function Dropdown({
  defaultFormFields,
  formFields,
  setFormFields,
  setIsDropdownOpen,
}: {
  defaultFormFields: IFormField[];
  formFields: IFormField[];
  setFormFields: (
    fields: IFormField[] | ((prev: IFormField[]) => IFormField[])
  ) => void;
  setIsDropdownOpen: (open: boolean) => void;
}) {
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [setIsDropdownOpen]);

  return (
    <div
      ref={dropdownRef}
      className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg z-50"
    >
      <div className="p-4">
        <div className="space-y-2 max-h-60 overflow-y-auto">
          {/* Custom Field option */}
          <div
            onClick={() => {
              const newCustomField: IFormField = {
                id: `my-${uuidv4()}`,
                title: "Custom Field",
                field_type: "text",
                required: false,
                verification_required: false,
                note: "",
                choices: null,
                is_default: false,
              };
              setFormFields((prev) => [...prev, newCustomField]);
              setIsDropdownOpen(false);
            }}
            className="p-4 border rounded-lg cursor-pointer transition-colors bg-[#FFF9F0] border-[#FFE5B3] hover:bg-[#FFF5E6]"
          >
            <div className="flex items-center gap-3">
              <div className="text-gray-400">
                <FiPlus size={20} />
              </div>
              <span className="font-medium">Custom Field</span>
            </div>
          </div>

          {/* Default form fields from API */}
          {defaultFormFields
            .filter((field) => !formFields.some((f) => f.title === field.title))
            .map((field) => (
              <div
                key={field.id}
                onClick={() => {
                  setFormFields((prev) => [
                    ...prev,
                    { ...field, id: field.id || uuidv4() },
                  ]);
                  setIsDropdownOpen(false);
                }}
                className="p-2 hover:bg-gray-50 rounded-lg cursor-pointer"
              >
                <span className="text-gray-700">{field.title}</span>
              </div>
            ))}

          {/* Fallback default fields if API fails */}
          {defaultFormFields.length === 0 && (
            <>
              <div
                onClick={() => {
                  const newField: IFormField = {
                    id: uuidv4(),
                    title: "Full Name",
                    field_type: "text",
                    required: true,
                    verification_required: false,
                    note: "",
                    choices: null,
                    is_default: true,
                  };
                  setFormFields((prev) => [...prev, newField]);
                  setIsDropdownOpen(false);
                }}
                className="p-2 hover:bg-gray-50 rounded-lg cursor-pointer"
              >
                <span className="text-gray-700">Full Name</span>
              </div>
              <div
                onClick={() => {
                  const newField: IFormField = {
                    id: uuidv4(),
                    title: "Email Address",
                    field_type: "email",
                    required: true,
                    verification_required: true,
                    note: "",
                    choices: null,
                    is_default: true,
                  };
                  setFormFields((prev) => [...prev, newField]);
                  setIsDropdownOpen(false);
                }}
                className="p-2 hover:bg-gray-50 rounded-lg cursor-pointer"
              >
                <span className="text-gray-700">Email Address</span>
              </div>
              <div
                onClick={() => {
                  const newField: IFormField = {
                    id: uuidv4(),
                    title: "Phone Number",
                    field_type: "tel",
                    required: true,
                    verification_required: true,
                    note: "",
                    choices: null,
                    is_default: true,
                  };
                  setFormFields((prev) => [...prev, newField]);
                  setIsDropdownOpen(false);
                }}
                className="p-2 hover:bg-gray-50 rounded-lg cursor-pointer"
              >
                <span className="text-gray-700">Phone Number</span>
              </div>
              <div
                onClick={() => {
                  const newField: IFormField = {
                    id: uuidv4(),
                    title: "Company Name",
                    field_type: "text",
                    required: false,
                    verification_required: false,
                    note: "",
                    choices: null,
                    is_default: true,
                  };
                  setFormFields((prev) => [...prev, newField]);
                  setIsDropdownOpen(false);
                }}
                className="p-2 hover:bg-gray-50 rounded-lg cursor-pointer"
              >
                <span className="text-gray-700">Company Name</span>
              </div>
              <div
                onClick={() => {
                  const newField: IFormField = {
                    id: uuidv4(),
                    title: "Message",
                    field_type: "text",
                    required: false,
                    verification_required: false,
                    note: "",
                    choices: null,
                    is_default: true,
                  };
                  setFormFields((prev) => [...prev, newField]);
                  setIsDropdownOpen(false);
                }}
                className="p-2 hover:bg-gray-50 rounded-lg cursor-pointer"
              >
                <span className="text-gray-700">Message</span>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
